(() => {
  const root = document.querySelector('[data-hagenuk-ai]');

  if (!root || root.dataset.initialized === 'true') {
    return;
  }

  const launcher = root.querySelector('[data-hagenuk-ai-open]');
  const panel = root.querySelector('[data-hagenuk-ai-panel]');
  const closeButton = root.querySelector('[data-hagenuk-ai-close]');
  const conversation = root.querySelector('[data-hagenuk-ai-conversation]');
  const suggestions = root.querySelector('[data-hagenuk-ai-suggestions]');
  const form = root.querySelector('[data-hagenuk-ai-form]');
  const input = root.querySelector('[data-hagenuk-ai-input]');
  const sendButton = root.querySelector('.hagenuk-ai__send');
  const locale = root.dataset.locale || document.documentElement.lang || 'de';
  const currency = root.dataset.currency || 'EUR';
  const avatarSrc = root.dataset.avatarSrc || '';
  const recipeData = window.HagenukAIRecipeData || { products: [], recipes: [] };
  const recipeProductsById = new Map((recipeData.products || []).map((product) => [product.id, product]));
  const history = [];
  const conversationState = {
    turn: 0,
    lastIntent: '',
    lastRecipeQuery: '',
    lastRecipeIds: []
  };
  let isBusy = false;
  let catalogCache = [];
  let catalogRequest = null;
  let lastProductSignature = '';
  let lastRecipeSignature = '';
  let viewportTrackingBound = false;
  let viewportFrame = 0;

  const syncViewportMetrics = () => {
    window.cancelAnimationFrame(viewportFrame);
    viewportFrame = window.requestAnimationFrame(() => {
      const viewport = window.visualViewport;
      const viewportHeight = Math.max(240, Math.round(viewport?.height || window.innerHeight));
      const viewportBottom = Math.max(
        0,
        Math.round(window.innerHeight - (viewport?.height || window.innerHeight) - (viewport?.offsetTop || 0))
      );

      root.style.setProperty('--hagenuk-ai-viewport-height', `${viewportHeight}px`);
      root.style.setProperty('--hagenuk-ai-viewport-bottom', `${viewportBottom}px`);
      viewportFrame = 0;
    });
  };

  const bindViewportTracking = () => {
    syncViewportMetrics();
    if (viewportTrackingBound) return;

    viewportTrackingBound = true;
    window.addEventListener('resize', syncViewportMetrics, { passive: true });
    window.visualViewport?.addEventListener('resize', syncViewportMetrics, { passive: true });
    window.visualViewport?.addEventListener('scroll', syncViewportMetrics, { passive: true });
  };

  const unbindViewportTracking = () => {
    if (!viewportTrackingBound) return;

    viewportTrackingBound = false;
    window.removeEventListener('resize', syncViewportMetrics);
    window.visualViewport?.removeEventListener('resize', syncViewportMetrics);
    window.visualViewport?.removeEventListener('scroll', syncViewportMetrics);
    window.cancelAnimationFrame(viewportFrame);
    viewportFrame = 0;
    root.style.removeProperty('--hagenuk-ai-viewport-height');
    root.style.removeProperty('--hagenuk-ai-viewport-bottom');
  };
  const productImageOverrides = (() => {
    const payload = root.querySelector('[data-hagenuk-ai-product-images]');

    if (!payload) {
      return {};
    }

    try {
      const parsed = JSON.parse(payload.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_error) {
      return {};
    }
  })();

  if (!launcher || !panel || !closeButton || !conversation || !form || !input || !sendButton) {
    return;
  }

  root.dataset.initialized = 'true';

  const labels = locale.startsWith('tr')
    ? {
        error: 'Şu anda bağlantı kuramadım. Lütfen biraz sonra yeniden deneyin.',
        cart: 'Sepete ekle',
        added: 'Sepete eklendi',
        unavailable: 'Şu anda mevcut değil',
        details: 'Ürünü incele',
        recipe: 'Tarifi aç',
        appliance: 'Cihazı incele',
        external: 'Dış bağlantıyı aç',
        loading: 'HAGENUK AI yanıt hazırlıyor',
        thinkingRecipe: 'Tarifler eşleştiriliyor',
        thinkingProduct: 'Ürün kataloğu kontrol ediliyor',
        thinkingConversation: 'Yanıt hazırlanıyor'
      }
    : locale.startsWith('en')
      ? {
          error: 'I could not connect right now. Please try again shortly.',
          cart: 'Add to cart',
          added: 'Added to cart',
          unavailable: 'Currently unavailable',
          details: 'View product',
          recipe: 'Open recipe',
          appliance: 'View appliance',
          external: 'Open external link',
          loading: 'HAGENUK AI is preparing a response',
          thinkingRecipe: 'Matching recipes',
          thinkingProduct: 'Checking the product catalog',
          thinkingConversation: 'Formulating a reply'
        }
      : {
          error: 'Die Verbindung ist gerade nicht möglich. Bitte versuchen Sie es gleich noch einmal.',
          cart: 'In den Warenkorb',
          added: 'Im Warenkorb',
          unavailable: 'Aktuell nicht verfügbar',
          details: 'Produkt ansehen',
          recipe: 'Rezept öffnen',
          appliance: 'Gerät ansehen',
          external: 'Externen Link öffnen',
          loading: 'HAGENUK AI bereitet eine Antwort vor',
          thinkingRecipe: 'Rezepte werden abgeglichen',
          thinkingProduct: 'Produktkatalog wird geprüft',
          thinkingConversation: 'Antwort wird formuliert'
        };

  const STOP_TERMS = new Set([
    'aber', 'als', 'auch', 'auf', 'aus', 'bei', 'bitte', 'brauche', 'das', 'der', 'die', 'ein', 'eine',
    'einem', 'einen', 'einer', 'eines', 'empfehlen', 'empfehlung', 'empfohlen', 'entscheiden',
    'entscheidung', 'fuer', 'fur', 'geraet', 'geraete', 'haben', 'ich', 'ist', 'klar', 'klare', 'konkret',
    'konkrete', 'mit', 'mir', 'modell', 'modelle', 'nach', 'noch', 'passende', 'passt', 'produkt',
    'produkte', 'sich', 'sind', 'suche', 'und', 'unsicher', 'von', 'was', 'welche', 'welcher', 'welches',
    'wir', 'zum', 'zur', 'bir', 'icin', 'ile', 'istiyorum', 'lazim', 'oneri', 'uygun', 've', 'and',
    'for', 'model', 'need', 'please', 'product', 'recommend', 'recommendation', 'the', 'unsure', 'want',
    'with', 'heute', 'machen', 'moechte', 'moechten', 'rezept', 'rezepte', 'rezeptidee', 'rezeptideen',
    'zubereiten', 'kochen', 'koche', 'essen', 'zeigen', 'zeige', 'gib', 'gerne', 'etwas', 'koennte',
    'hagenuk', 'will', 'wuerde'
  ]);

  const SYNONYMS = new Map([
    ['airfryer', ['heissluftfritteuse', 'heissluftofen', 'fritteuse']],
    ['heissluft', ['airfryer', 'heissluftfritteuse', 'heissluftofen', 'fritteuse']],
    ['fritoz', ['airfryer', 'heissluftfritteuse', 'fritteuse']],
    ['kaffee', ['kaffeemaschine', 'espresso', 'mahlwerk']],
    ['kahve', ['kaffee', 'kaffeemaschine', 'espresso']],
    ['espresso', ['kaffee', 'kaffeemaschine', 'milchaufschaeumer']],
    ['eis', ['eiscreme', 'eismaschine', 'dessert', 'slush']],
    ['eismaschine', ['eiscreme', 'dessertmaschine', 'dessert', 'slush']],
    ['eismaschinen', ['eiscreme', 'eismaschine', 'dessertmaschine', 'slush']],
    ['dessertmaschine', ['eiscreme', 'eismaschine', 'dessert', 'slush']],
    ['dessertmaschinen', ['eiscreme', 'eismaschine', 'dessert', 'slush']],
    ['dessert', ['eiscreme', 'eismaschine', 'dessertmaschine', 'slush']],
    ['dondurma', ['eiscreme', 'eismaschine', 'dessert', 'slush']],
    ['vegane', ['vegan', 'pflanzlich']],
    ['veganes', ['vegan', 'pflanzlich']],
    ['veganer', ['vegan', 'pflanzlich']],
    ['pflanzliche', ['vegan', 'pflanzlich']],
    ['pflanzliches', ['vegan', 'pflanzlich']],
    ['kartoffeln', ['kartoffel']],
    ['gerichte', ['gericht']],
    ['mixer', ['smoothie', 'stabmixer', 'kuechenmaschine']],
    ['backen', ['kuechenmaschine', 'handmixer', 'mixer', 'teig']],
    ['staubsauger', ['handstaubsauger', 'reinigung', 'haushalt']],
    ['temizlik', ['staubsauger', 'handstaubsauger', 'reinigung']]
  ]);

  const DESSERT_MACHINE_HANDLES = [
    'hagenuk-eiscreme-dessertmaschine-icm-500-05lb-800w',
    'hagenuk-slush-ice-maschine-sim-200-19'
  ];

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const stripHtml = (value) => {
    const template = document.createElement('template');
    template.innerHTML = String(value || '');
    return (template.content.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const truncate = (value, maxLength) => {
    const compact = String(value || '').replace(/\s+/g, ' ').trim();

    if (compact.length <= maxLength) {
      return compact;
    }

    const shortened = compact.slice(0, maxLength - 1);
    const lastSpace = shortened.lastIndexOf(' ');
    return `${shortened.slice(0, Math.max(lastSpace, maxLength * .7)).trim()}…`;
  };

  const currentProductHandle = (() => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  })();

  const mapCatalogProduct = (product) => {
    const handle = String(product.handle || '');
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const preferredVariant = variants.find((variant) => variant.available) || variants[0] || null;
    const images = Array.isArray(product.images) ? product.images : [];
    const featuredImage = images[0] || null;
    const description = stripHtml(product.body_html);
    const tags = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
    const productType = String(product.product_type || '');

    return {
      title: String(product.title || ''),
      handle,
      url: `/products/${encodeURIComponent(handle)}`,
      image: productImageOverrides[handle] || featuredImage?.src || '',
      image_alt: featuredImage?.alt || product.title || '',
      price: {
        amount: preferredVariant?.price || '',
        currency
      },
      available: variants.some((variant) => Boolean(variant.available)),
      variant_id: preferredVariant?.id ? String(preferredVariant.id) : '',
      _description: description,
      _search: {
        title: normalize(product.title),
        type: normalize(productType),
        tags: normalize(tags),
        description: normalize(description)
      }
    };
  };

  const catalogUrl = () => {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const rootPath = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const url = new URL(`${rootPath}products.json`, window.location.origin);
    url.searchParams.set('limit', '100');
    return url.toString();
  };

  const loadCatalog = async () => {
    if (catalogCache.length > 0) {
      return catalogCache;
    }

    if (catalogRequest) {
      return catalogRequest;
    }

    catalogRequest = (async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 5000);

      try {
        const response = await fetch(catalogUrl(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Catalog request failed with ${response.status}`);
        }

        const data = await response.json();
        const products = Array.isArray(data.products)
          ? data.products.map(mapCatalogProduct).filter((product) => product.handle && product.title)
          : [];

        if (products.length > 0) {
          catalogCache = products;
        }

        return products;
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    try {
      return await catalogRequest;
    } catch (_error) {
      return [];
    } finally {
      catalogRequest = null;
    }
  };

  const queryTerms = (query) => {
    const baseTerms = normalize(query)
      .split(' ')
      .filter((term) => term.length > 2 && !STOP_TERMS.has(term));
    const terms = new Set(baseTerms);

    baseTerms.forEach((term) => {
      (SYNONYMS.get(term) || []).forEach((synonym) => terms.add(synonym));
    });

    return [...terms];
  };

  const containsTerm = (text, term) => term.length <= 3
    ? text.split(' ').includes(term)
    : text.includes(term);

  const searchCatalog = (catalog, query, maxResults = 3) => {
    const terms = queryTerms(query);

    if (terms.length === 0) {
      return [];
    }

    const scored = catalog.map((product) => {
      let score = product.available ? 1 : 0;

      terms.forEach((term) => {
        if (product._search.title === term) score += 12;
        if (containsTerm(product._search.title, term)) score += 8;
        if (containsTerm(product._search.type, term)) score += 5;
        if (containsTerm(product._search.tags, term)) score += 5;
        if (containsTerm(product._search.description, term)) score += 2;
      });

      return { product, score };
    }).filter((item) => item.score > 1);

    scored.sort((left, right) => right.score - left.score || left.product.title.localeCompare(right.product.title, 'de'));

    if (scored.length === 0) {
      return [];
    }

    const relevanceFloor = Math.max(3, scored[0].score * .35);
    return scored
      .filter((item) => item.score >= relevanceFloor)
      .slice(0, maxResults)
      .map((item) => item.product);
  };

  const productsByHandles = (catalog, handles) => {
    const products = new Map(catalog.map((product) => [product.handle, product]));
    return handles.map((handle) => products.get(handle)).filter(Boolean).slice(0, 3);
  };

  const recipeIndex = (recipeData.recipes || []).map((recipe) => {
    const product = recipeProductsById.get(recipe.productId) || {};
    const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
    const tags = Array.isArray(recipe.tags) ? recipe.tags : [];
    const occasion = Array.isArray(recipe.occasion) ? recipe.occasion : [];
    const routeRoot = window.Shopify?.routes?.root || '/';
    const rootPath = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;

    return {
      ...recipe,
      product,
      image: product.image || '',
      url: `${rootPath}?recipe=${encodeURIComponent(recipe.id)}#rezepte`,
      _search: {
        title: normalize(recipe.title),
        hook: normalize(recipe.hook),
        category: normalize(recipe.category),
        diet: normalize(`${recipe.diet} ${recipe.veganSupported ? 'vegan pflanzlich' : ''}`),
        product: normalize(`${recipe.productName} ${product.shortName || ''} ${product.handle || ''} ${product.type || ''}`),
        ingredients: normalize(ingredients.join(' ')),
        tags: normalize(`${tags.join(' ')} ${occasion.join(' ')}`),
        settings: normalize(recipe.applianceSettings)
      }
    };
  });

  const GREETING_PATTERN = /^(hallo|hallo zusammen|hi|hey|hello|guten morgen|guten tag|guten abend|moin|servus|gruess gott|merhaba|selam)$/;
  const SMALL_TALK_PATTERN = /\b(wie geht|wer bist|dein name|was kannst du|bist du ein robot|bist du eine ki|danke|vielen dank|besten dank|tschuess|auf wiedersehen|bis bald|alles klar|super|perfekt|okay|ok)\b/;
  const RECIPE_PATTERN = /\b(rezept\w*|zubereiten|kochen|backen|gericht\w*|essen|hunger|vegan\w*|pflanzlich\w*|vegetarisch\w*|zutaten|fruehstueck|mittagessen|abendessen|snack|salat|suppe|kuchen|brot|pizza|pommes|kartoffel\w*|haehnchen|tofu|fisch|smoothie|shake|sorbet|gelato|frozen yogurt|frozen yoghurt|slush|drink|dessert|joghurt|yogurt|recipe\w*|cook\w*|meal|food|vegetarian|breakfast|dinner|tarif\w*|yemek|kahvalti|aksam yemegi|tatli|dondurma)\b/;
  const COMPARISON_PATTERN = /\b(vergleichen|vergleich|unterschied|gegenueber|welches modell|welche maschine|beide modelle|beide maschinen|compare|difference|karsilastir)\b/;

  const classifyIntent = (message) => {
    const normalizedMessage = normalize(message);

    if (GREETING_PATTERN.test(normalizedMessage)) return 'greeting';
    if (SMALL_TALK_PATTERN.test(normalizedMessage)) return 'smalltalk';
    if (COMPARISON_PATTERN.test(normalizedMessage)) return 'comparison';
    if (RECIPE_PATTERN.test(normalizedMessage)) return 'recipe';
    if (conversationState.lastIntent === 'recipe' && /\b(mehr|andere|schneller|einfacher|ohne|fuer|personen|noch eins|davon)\b/.test(normalizedMessage)) return 'recipe';
    if (/\b(kaufen|preis|lieferbar|verfuegbar|produkt|geraet|maschine|airfryer|heissluft|kaffee|mixer|toaster|wasserkocher|staubsauger)\b/.test(normalizedMessage)) return 'product';
    return 'general';
  };

  const selectDiverseRecipes = (recipes, maxResults, rotate = true) => {
    if (recipes.length <= maxResults) return recipes;

    const selected = [];
    const usedProducts = new Set();
    const usedCategories = new Set();
    const start = rotate ? (conversationState.turn * 11) % recipes.length : 0;
    const ordered = [...recipes.slice(start), ...recipes.slice(0, start)];

    ordered.forEach((recipe) => {
      if (selected.length >= maxResults) return;
      if (!usedProducts.has(recipe.productId) && !usedCategories.has(recipe.category)) {
        selected.push(recipe);
        usedProducts.add(recipe.productId);
        usedCategories.add(recipe.category);
      }
    });

    ordered.forEach((recipe) => {
      if (selected.length < maxResults && !selected.includes(recipe)) selected.push(recipe);
    });

    return selected.slice(0, maxResults);
  };

  const formatRecipeDuration = (minutes) => {
    const total = Number(minutes) || 0;
    if (total < 60) return `${total} Min.`;

    const hours = Math.floor(total / 60);
    const remainingMinutes = total % 60;
    return remainingMinutes ? `${hours} Std. ${remainingMinutes} Min.` : `${hours} Std.`;
  };

  const searchRecipes = (query, maxResults = 3, preferredProductIds = []) => {
    const normalizedQuery = normalize(query);
    const terms = queryTerms(query);
    const wantsVegan = /\b(vegan\w*|pflanzlich\w*)\b/.test(normalizedQuery);
    const wantsVegetarian = /\b(vegetarisch)\b/.test(normalizedQuery);
    const maxTimeMatch = normalizedQuery.match(/\b(?:maximal|hoechstens|unter|bis)\s*(\d{1,3})\s*(?:min|minuten)\b/);
    const maxTime = maxTimeMatch ? Number(maxTimeMatch[1]) : 0;
    const preferred = new Set(preferredProductIds);
    const explicitCategories = new Set([...new Set(recipeIndex.map((recipe) => recipe.category))]
      .filter((category) => {
        const normalizedCategory = normalize(category);
        if (normalizedCategory.length <= 4) return false;
        return normalizedCategory.includes(' ')
          ? normalizedQuery.includes(normalizedCategory)
          : normalizedQuery.split(' ').includes(normalizedCategory);
      }));
    const explicitProductIds = new Set((recipeData.products || [])
      .filter((product) => {
        const names = [product.name, product.shortName, product.handle]
          .map(normalize)
          .filter((name) => name.length > 5);
        return names.some((name) => normalizedQuery.includes(name));
      })
      .map((product) => product.id));
    const eligible = recipeIndex.filter((recipe) => {
      if (explicitProductIds.size && !explicitProductIds.has(recipe.productId)) return false;
      if (explicitCategories.size && !explicitCategories.has(recipe.category)) return false;
      if (wantsVegan && recipe.diet !== 'vegan' && !recipe.veganSupported) return false;
      if (wantsVegetarian && !['vegan', 'vegetarisch'].includes(recipe.diet)) return false;
      if (maxTime && Number(recipe.totalMinutes) > maxTime) return false;
      return true;
    });

    const scored = eligible.map((recipe) => {
      let score = preferred.has(recipe.productId) ? 18 : 0;
      if (wantsVegan && recipe.diet === 'vegan') score += 12;
      if (wantsVegetarian && recipe.diet === 'vegetarisch') score += 8;

      terms.forEach((term) => {
        if (recipe._search.title === term) score += 18;
        if (containsTerm(recipe._search.title, term)) score += 12;
        if (containsTerm(recipe._search.product, term)) score += 10;
        if (containsTerm(recipe._search.ingredients, term)) score += 8;
        if (containsTerm(recipe._search.category, term)) score += 7;
        if (containsTerm(recipe._search.diet, term)) score += 6;
        if (containsTerm(recipe._search.tags, term)) score += 4;
        if (containsTerm(recipe._search.hook, term)) score += 2;
        if (containsTerm(recipe._search.settings, term)) score += 1;
      });

      return { recipe, score };
    });

    scored.sort((left, right) => right.score - left.score
      || Number(left.recipe.totalMinutes) - Number(right.recipe.totalMinutes)
      || left.recipe.title.localeCompare(right.recipe.title, 'de'));

    const meaningfulTerms = terms.filter((term) => !['vegan', 'pflanzlich', 'vegetarisch'].includes(term));
    if (meaningfulTerms.length || preferred.size) {
      const relevant = scored.filter((item) => item.score > 0);
      if (relevant.length) {
        const relevantRecipes = relevant.map((item) => item.recipe);
        return explicitProductIds.size
          ? relevantRecipes.slice(0, maxResults)
          : selectDiverseRecipes(relevantRecipes, maxResults, false);
      }
    }

    return selectDiverseRecipes(scored.map((item) => item.recipe), maxResults);
  };

  const productDescription = (product) => {
    if (!product?._description) {
      return '';
    }

    const sentences = product._description.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
    return truncate(sentences.slice(0, 2).join(' '), 360);
  };

  const getPeopleCount = (message) => {
    if (/\b1\s+2\b/.test(message)) return 2;
    if (/\b3\s+4\b/.test(message)) return 4;
    if (/\b5\s+(oder|und)\s+mehr\b/.test(message)) return 5;

    const numeric = message.match(/\b([1-9]|10)\s*(person|personen)\b/);
    if (numeric) return Number(numeric[1]);

    const written = [
      ['eins', 1], ['eine', 1], ['zwei', 2], ['drei', 3],
      ['vier', 4], ['fuenf', 5], ['sechs', 6]
    ].find(([word]) => new RegExp(`\\b${word}\\b`).test(message));

    return written?.[1] || 0;
  };

  const baseResult = (reply, products = [], suggestionItems = [], externalLink = null, recipes = []) => ({
    reply,
    products,
    recipes,
    suggestions: suggestionItems,
    external_link: externalLink
  });

  const buildGermanRecipeResult = (message, lastRecommendedProducts = []) => {
    const previousRecipeProducts = conversationState.lastRecipeIds
      .map((recipeId) => recipeIndex.find((recipe) => recipe.id === recipeId)?.productId)
      .filter(Boolean);
    const recommendedProductIds = lastRecommendedProducts
      .map((product) => (recipeData.products || []).find((item) => item.handle === product.handle || item.url === product.url)?.id)
      .filter(Boolean);
    const preferredProductIds = [...new Set([...recommendedProductIds, ...previousRecipeProducts])];
    const isShortFollowUp = normalize(message).split(' ').length <= 4 && conversationState.lastIntent === 'recipe';
    const contextualQuery = isShortFollowUp && conversationState.lastRecipeQuery
      ? `${conversationState.lastRecipeQuery} ${message}`
      : message;
    const asksForVegan = /\b(vegan\w*|pflanzlich\w*)\b/.test(normalize(contextualQuery));
    const recipes = searchRecipes(contextualQuery, 3, preferredProductIds).map((recipe) => ({
      ...recipe,
      displayDiet: asksForVegan && recipe.diet !== 'vegan' && recipe.veganSupported
        ? 'Vegane Variante'
        : ''
    }));

    if (!recipes.length) {
      return baseResult(
        'Dazu finde ich im aktuellen Hagenuk Küchenatelier noch keine passende Fassung. Nennen Sie mir gern eine Hauptzutat, die gewünschte Zeit oder Ihr Gerät; dann suche ich gezielter.',
        [],
        ['Vegane Ideen', 'In höchstens 15 Minuten', 'Nach Gerät auswählen']
      );
    }

    conversationState.lastRecipeQuery = contextualQuery;
    const first = recipes[0];
    const dietLabel = first.displayDiet
      ? 'mit veganer Variante'
      : first.diet === 'vegan'
        ? 'vegan'
        : first.diet === 'vegetarisch'
          ? 'vegetarisch'
          : 'mit Fleisch oder Fisch';
    const ingredients = (first.ingredients || []).slice(0, 4).join(', ');
    const veganGuidance = first.displayDiet && first.veganOption
      ? ` Vegane Anpassung: ${first.veganOption}`
      : '';
    const exactTitleMatch = normalize(first.title).includes(normalize(message)) || normalize(message).includes(normalize(first.title));
    const sameAppliance = recipes.every((recipe) => recipe.productId === first.productId);
    const reply = recipes.length === 1 || exactTitleMatch
      ? `${first.title} passt gut: ${formatRecipeDuration(first.totalMinutes)}, ${dietLabel}, zubereitet mit ${first.productName}. Die wichtigsten Zutaten sind ${ingredients}.${veganGuidance} Die vollständige Küchenfassung und das passende Gerät finden Sie direkt in der Karte.`
      : `Ich habe ${recipes.length} passende Küchenfassungen gefunden. Mein erster Vorschlag ist ${first.title} mit ${first.productName} (${formatRecipeDuration(first.totalMinutes)}, ${dietLabel}).${veganGuidance} ${sameAppliance ? 'Die weiteren Ideen bleiben bei diesem Gerät und variieren das Gericht.' : 'Die weiteren Ideen variieren Gericht und Gerät, damit Sie wirklich auswählen können.'}`;
    const suggestions = [
      /\b(vegan|pflanzlich)\b/.test(normalize(message)) ? 'Noch eine vegane Idee' : 'Nur vegane Ideen',
      'In höchstens 15 Minuten',
      `Mehr mit ${first.product?.shortName || first.productName}`
    ];

    return baseResult(reply, [], suggestions, null, recipes);
  };

  const germanAvaResponse = (message, catalog) => {
    const normalizedMessage = normalize(message);
    const priorUserMessages = history
      .filter((item) => item.role === 'user')
      .map((item) => normalize(item.content));
    const priorUserConversation = priorUserMessages.join(' ');
    const lastProductTurn = [...history]
      .reverse()
      .find((item) => item.role === 'assistant' && Array.isArray(item.product_handles) && item.product_handles.length > 0);
    const lastRecommendedProducts = productsByHandles(catalog, lastProductTurn?.product_handles || []);
    const dessertMachines = productsByHandles(catalog, DESSERT_MACHINE_HANDLES);
    const lastDessertRecommendations = lastRecommendedProducts.filter((product) => DESSERT_MACHINE_HANDLES.includes(product.handle));
    const priorDessertContext = /\b(eis|eiscreme|eismaschine|eismaschinen|dessert|dessertmaschine|dessertmaschinen|slush|frozen yogurt)\b/.test(priorUserConversation)
      || lastDessertRecommendations.length > 0;
    const isGreeting = GREETING_PATTERN.test(normalizedMessage);
    const greetingCount = priorUserMessages.filter((item) => GREETING_PATTERN.test(item)).length;

    if (isGreeting) {
      const greetingReplies = [
        'Ah, hallo - schön, dass Sie da sind. Ich bin HAGENUK AI. Wir können über ein Gericht, eine Zutat oder das passende Gerät sprechen. Worauf haben Sie heute Lust?',
        'Hallo noch einmal. Ich bin ganz Ohr. Möchten Sie diesmal mit einem Gericht beginnen, ein Gerät auswählen oder zwei Modelle vergleichen?',
        'Da sind Sie wieder - hallo. Sagen Sie einfach frei heraus, was gerade in Ihrer Küche ansteht. Ich sortiere dann Rezept und passendes Hagenuk Gerät für Sie.'
      ];
      return baseResult(
        greetingReplies[Math.min(greetingCount, greetingReplies.length - 1)],
        [],
        ['Was koche ich heute?', 'Vegane Idee', 'Gerät für meinen Alltag']
      );
    }

    if (/^(wie geht es dir|wie gehts|alles gut)$/.test(normalizedMessage)) {
      return baseResult(
        'Mir geht es gut, danke. Ich habe gerade 276 Hagenuk Küchenfassungen im Blick - also ziemlich viele Ideen im Kopf. Haben Sie eher Appetit auf etwas Herzhaftes, Süßes oder Schnelles?',
        [],
        ['Etwas Herzhaftes', 'Etwas Süßes', 'Unter 15 Minuten']
      );
    }

    if (/\b(wer bist du|dein name|bist du ein robot|bist du eine ki)\b/.test(normalizedMessage)) {
      return baseResult(
        'Ich bin HAGENUK AI, der digitale Küchen- und Produktberater von Hagenuk. Mein Gesicht kennen Sie aus dem Küchenatelier. Ich verbinde Ihre Idee mit den vorhandenen Rezeptfassungen und prüfe Produktangaben im aktuellen Hagenuk Katalog.',
        [],
        ['Was kannst du kochen?', 'Passendes Gerät finden', 'Vegan entdecken']
      );
    }

    if (/\b(was kannst du|wobei hilfst du|wie kannst du helfen)\b/.test(normalizedMessage)) {
      return baseResult(
        'Sie können ganz normal mit mir sprechen: nennen Sie ein Gericht, eine Zutat, Ihre Ernährungsweise oder ein Gerät. Ich suche passende Rezepte, ordne das verwendete Modell zu, vergleiche Geräte und kann Sie von der Idee bis zur Produktseite begleiten.',
        [],
        ['Ich habe eine Zutat', 'Rezept für mein Gerät', 'Modelle vergleichen']
      );
    }

    if (/^(danke|danke schoen|vielen dank|besten dank)$/.test(normalizedMessage)) {
      return baseResult(
        'Sehr gern. Möchten Sie die Empfehlung noch genauer eingrenzen, ein Rezept für das Gerät bekommen oder direkt das passende Produkt ansehen?',
        [],
        ['Empfehlung verfeinern', 'Passendes Rezept', 'Produkt direkt ansehen']
      );
    }

    if (/^(tschuess|auf wiedersehen|bis bald)$/.test(normalizedMessage)) {
      return baseResult('Auf Wiedersehen. Bei Fragen zu einem Gerät, einem Unterschied oder einem Rezept bin ich direkt hier für Sie.');
    }

    if (/^(ok|okay|alles klar|super|perfekt|gut)$/.test(normalizedMessage)) {
      return baseResult(
        'Gut. Dann machen wir genau dort weiter, wo es für Sie sinnvoll ist. Soll ich die Auswahl verfeinern, ein passendes Rezept zeigen oder direkt zum Gerät führen?',
        [],
        ['Auswahl verfeinern', 'Passendes Rezept', 'Zum Gerät']
      );
    }

    if (classifyIntent(message) === 'recipe') {
      return buildGermanRecipeResult(message, lastRecommendedProducts);
    }

    if (/\b(keine idee|weiss nicht|ueberrasch mich|such du aus|entscheide du)\b/.test(normalizedMessage)) {
      return buildGermanRecipeResult('Was könnte ich heute zubereiten?', lastRecommendedProducts);
    }

    const asksForOtherDessertMachine = /\b(die andere|andere eismaschine|zweite eismaschine|noch eine|beide|alle eismaschinen|alternativen vergleichen|mit alternativen vergleichen)\b/.test(normalizedMessage)
      && !/\b(preis|preise|kostet)\b/.test(normalizedMessage)
      && (priorDessertContext || /\b(eismaschine|eismaschinen|dessertmaschine|dessertmaschinen|slush)\b/.test(normalizedMessage));

    if (asksForOtherDessertMachine) {
      const lastHandle = lastDessertRecommendations[0]?.handle;
      const orderedHandles = lastHandle === DESSERT_MACHINE_HANDLES[0]
        ? [DESSERT_MACHINE_HANDLES[1], DESSERT_MACHINE_HANDLES[0]]
        : DESSERT_MACHINE_HANDLES;

      return baseResult(
        'Im aktuellen Hagenuk Sortiment gibt es zwei unterschiedliche Eisgeräte: Die Hagenuk Eiscreme & Dessertmaschine ist für cremige Ergebnisse wie Eiscreme, Gelato, Sorbet und Frozen Yogurt gedacht. Die Slush Ice Maschine arbeitet mit Direktkühlung und ist für Slush, Milchshakes und Frozen-Cocktails mit bis zu 2,2 Litern ausgelegt. Für cremige Desserts wählen Sie die Eiscreme & Dessertmaschine; für gekühlte Getränke und größere Mengen die Slush Ice Maschine.',
        productsByHandles(catalog, orderedHandles),
        ['Beide Preise anzeigen', 'Frozen-Yogurt-Rezept', 'Slush ohne Alkohol']
      );
    }

    if (lastRecommendedProducts.length > 0 && /\b(preis|preise|kostet|verfuegbar|lieferbar|auf lager|produkt ansehen|produkt direkt ansehen)\b/.test(normalizedMessage)) {
      if (lastRecommendedProducts.length > 1) {
        const summaries = lastRecommendedProducts.map((product) => {
          const price = formatPrice(product.price);
          const availability = product.available ? 'verfügbar' : 'nicht verfügbar';
          return `${product.title}: ${price || 'Preis auf der Produktseite'}, ${availability}`;
        });

        return baseResult(
          `${summaries.join('. ')}. Über die Produktkarten können Sie beide Modelle direkt vergleichen und das passende verfügbare Gerät öffnen oder in den Warenkorb legen.`,
          lastRecommendedProducts,
          ['Unterschiede erklären', 'Ein Modell empfehlen', 'Anwendung auswählen']
        );
      }

      const product = lastRecommendedProducts[0];
      const price = formatPrice(product.price);
      const availability = product.available ? 'aktuell verfügbar' : 'aktuell nicht verfügbar';

      return baseResult(
        `${product.title} ist ${availability}${price ? ` und wird derzeit mit ${price} angezeigt` : ''}. Über die Produktkarte können Sie alle Details öffnen oder das verfügbare Modell direkt in den Warenkorb legen.`,
        lastRecommendedProducts,
        ['Anwendung erklären', 'Mit Alternativen vergleichen', 'Kaufempfehlung bestätigen']
      );
    }

    if (lastRecommendedProducts.length > 0 && /\b(anwendung erklaeren|was kann es|was kann das geraet|wie funktioniert es)\b/.test(normalizedMessage)) {
      const product = lastRecommendedProducts[0];
      const description = productDescription(product);

      return baseResult(
        description
          ? `${product.title}: ${description} Welche konkrete Anwendung möchten Sie damit umsetzen?`
          : `Gern. Welche konkrete Anwendung möchten Sie mit ${product.title} umsetzen? Dann erkläre ich Ihnen den passenden Ablauf.`,
        lastRecommendedProducts,
        ['Praktisches Beispiel', 'Preis und Verfügbarkeit', 'Mit Alternativen vergleichen']
      );
    }

    if (lastRecommendedProducts.length > 0 && /\b(alternativen vergleichen|mit alternativen vergleichen)\b/.test(normalizedMessage)) {
      if (lastRecommendedProducts.length > 1) {
        return baseResult(
          `Die zuletzt empfohlenen Modelle sind ${lastRecommendedProducts.map((product) => product.title).join(' und ')}. Sagen Sie mir, welche Eigenschaft für Sie entscheidet - Größe, Zubereitungsart oder mehrere Speisen gleichzeitig - dann nenne ich Ihnen den klaren Favoriten.`,
          lastRecommendedProducts,
          ['Größe ist entscheidend', 'Mehrere Speisen gleichzeitig', 'Ein Modell empfehlen']
        );
      }

      return baseResult(
        `Im aktuellen Hagenuk Sortiment ist ${lastRecommendedProducts[0].title} die direkte Übereinstimmung zu Ihrer letzten Frage. Nennen Sie mir die Funktion, bei der Sie eine Alternative suchen; dann prüfe ich das gesamte Sortiment gezielt danach.`,
        lastRecommendedProducts,
        ['Kompakteres Modell', 'Andere Zubereitungsart', 'Preis und Verfügbarkeit']
      );
    }

    const asksForClearRecommendation = /\b(unsicher|klare empfehlung|konkrete empfehlung|was empfehlen sie|welches passt|welches soll ich nehmen|ein modell empfehlen|kaufempfehlung|entscheidung|entscheiden)\b/.test(normalizedMessage);

    if (lastRecommendedProducts.length > 0 && asksForClearRecommendation) {
      if (lastDessertRecommendations.length > 1) {
        return baseResult(
          'Aus dem bisherigen Vergleich ergibt sich eine klare Trennung: Wählen Sie die Hagenuk Eiscreme & Dessertmaschine für Eiscreme, Gelato, Sorbet und Frozen Yogurt. Wählen Sie die Slush Ice Maschine für Slush, Milchshakes, Frozen-Cocktails und größere Getränkemengen. Was bereiten Sie häufiger zu: cremige Desserts oder gekühlte Getränke?',
          dessertMachines,
          ['Cremige Desserts', 'Gekühlte Getränke', 'Beide Preise anzeigen']
        );
      }

      if (lastRecommendedProducts.length === 1) {
        return baseResult(
          `Auf Basis Ihrer bisherigen Angaben ist ${lastRecommendedProducts[0].title} meine klare Empfehlung. Möchten Sie vor dem Kauf noch Anwendung, Preis und Verfügbarkeit oder eine direkte Alternative prüfen?`,
          lastRecommendedProducts,
          ['Anwendung erklären', 'Preis und Verfügbarkeit', 'Alternative prüfen']
        );
      }

      return baseResult(
        'Für eine klare Entscheidung brauche ich nur das wichtigste Kriterium: gewünschtes Ergebnis, Haushaltsgröße oder eine bestimmte Funktion. Danach nenne ich Ihnen aus den zuletzt gezeigten Modellen genau einen Favoriten.',
        lastRecommendedProducts,
        ['Ergebnis ist entscheidend', 'Haushaltsgröße', 'Bestimmte Funktion']
      );
    }

    if (asksForClearRecommendation) {
      return baseResult(
        'Gern gebe ich Ihnen eine klare Empfehlung. In welchem Bereich suchen Sie: Heißluftgaren, Kaffee, Eis und Desserts oder Küchenvorbereitung?',
        [],
        ['Airfryer-Beratung', 'Eis & Dessert', 'Kaffeeberatung']
      );
    }

    const hasCurrentCategory = /\b(airfryer|heissluft|fritteuse|kaffee|espresso|eis|eiscreme|eismaschine|eismaschinen|dessert|dessertmaschine|dessertmaschinen|slush|mixer|staubsauger|kuechenmaschine)\b/.test(normalizedMessage);

    if (/\b(kaufberatung|persoenliche beratung|produktberatung)\b/.test(normalizedMessage) && !hasCurrentCategory) {
      return baseResult(
        'Sehr gern. Wofür soll das Gerät hauptsächlich eingesetzt werden: schnelleres Heißluftgaren, Kaffee, Eis und Desserts oder Küchenvorbereitung?',
        [],
        ['Airfryer-Beratung', 'Eis & Dessert', 'Kaffeeberatung']
      );
    }

    if (/\b(produktvergleich|geraete vergleichen|modelle vergleichen)\b/.test(normalizedMessage) && !hasCurrentCategory) {
      return baseResult(
        'Gern vergleiche ich die passenden Modelle. Welche Produktgruppe möchten Sie gegenüberstellen? Danach ordne ich die Unterschiede nach Anwendung und den aktuellen Produktinformationen ein.',
        [],
        ['Airfryer vergleichen', 'Eismaschinen vergleichen', 'Kaffeemaschinen vergleichen']
      );
    }

    if (/\b(rezept und anwendung|rezept oder anwendungsidee|rezeptidee|anwendungsidee)\b/.test(normalizedMessage) && !hasCurrentCategory) {
      return baseResult(
        'Sehr gern. Für welches Hagenuk Gerät wünschen Sie ein Rezept oder eine Anwendungsidee, und welches Ergebnis möchten Sie erreichen?',
        [],
        ['Airfryer-Rezept', 'Frozen-Yogurt-Rezept', 'Kaffee-Idee']
      );
    }

    const airfryerContext = /\b(airfryer|heissluft|fritteuse|rotisserie|frittierkorb)\b/.test(normalizedMessage)
      || (/\b(person|personen|haushalt|familie|leise|koerbe|korb)\b/.test(normalizedMessage)
        && /\b(airfryer|heissluft|fritteuse)\b/.test(priorUserConversation));

    if (airfryerContext) {
      const handles = [
        'hagenuk-doppel-heissluftfritteuse',
        'heissluftfritteuse-mit-rotisserie',
        'infrarot-heissluftfritteuse'
      ];

      if (/\b(vergleich|vergleichen|unterschied|welche|welcher)\b/.test(normalizedMessage)) {
        return baseResult(
          'Die Doppel-Heißluftfritteuse bietet 11 Liter und zwei getrennte 5,5-Liter-Körbe - praktisch für zwei Gerichte gleichzeitig. Das 8-Liter-Modell passt besser, wenn Rotisserie und ein rotierender Edelstahlkorb wichtig sind. Die 5-Liter-Infrarot-Fritteuse ist die kompaktere Wahl mit 12 Programmen und Sichtfenster.',
          productsByHandles(catalog, handles),
          ['Für wie viele Personen?', 'Sind zwei Körbe wichtig?', 'Rotisserie gewünscht?']
        );
      }

      if (/\b(rezept|kartoffel|pommes|haehnchen|gemuese)\b/.test(normalizedMessage)) {
        return baseResult(
          'Als einfache Airfryer-Idee eignen sich gewürzte Kartoffelspalten: gleichmäßig schneiden, mit wenig Öl und Gewürzen mischen und bei 190 °C zunächst etwa 18-24 Minuten garen. Das ist ein praktischer Startwert, kein gerätespezifisches Automatikprogramm; zwischendurch wenden und den Gargrad prüfen. Für eine genaue Menge brauche ich nur Ihre Haushaltsgröße.',
          productsByHandles(catalog, handles),
          ['Rezept für zwei Personen', 'Gemüse im Airfryer', 'Welches Modell passt?']
        );
      }

      if (/\b(leise|leiser|lautstaerke)\b/.test(normalizedMessage)) {
        return baseResult(
          'Wenn leises Garen besonders wichtig ist, würde ich zuerst die 5-Liter-Infrarot-Heißluftfritteuse ansehen. In der aktuellen Produktbeschreibung wird ausdrücklich leises Garen ohne lautes Gebläse genannt.',
          productsByHandles(catalog, ['infrarot-heissluftfritteuse']),
          ['Für wie viele Personen?', 'Programme ansehen', 'Mit Rotisserie vergleichen']
        );
      }

      if (/\b(rotisserie|drehkorb|ganzes haehnchen)\b/.test(normalizedMessage)) {
        return baseResult(
          'Dann ist die 8-Liter-Heißluftfritteuse mit Rotisserie die passendere Wahl. Sie ist laut aktueller Produktbeschreibung für den Drehspieß und den rotierenden Edelstahlkorb ausgelegt.',
          productsByHandles(catalog, ['heissluftfritteuse-mit-rotisserie']),
          ['Rotisserie-Gerichte', 'Mit Doppelkorb vergleichen', 'Produkt ansehen']
        );
      }

      if (/\b(zwei koerbe|doppelkorb|zwei gerichte|gleichzeitig)\b/.test(normalizedMessage)) {
        return baseResult(
          'Für zwei Gerichte gleichzeitig ist die Hagenuk Doppel-Heißluftfritteuse die klare Wahl. Ihre beiden 5,5-Liter-Körbe lassen sich unabhängig steuern.',
          productsByHandles(catalog, ['hagenuk-doppel-heissluftfritteuse']),
          ['Für eine Familie geeignet?', 'Mit Rotisserie vergleichen', 'Produkt ansehen']
        );
      }

      const people = getPeopleCount(normalizedMessage);

      if (people >= 5 || /\b(familie|familien)\b/.test(normalizedMessage)) {
        return baseResult(
          'Für einen größeren Familienhaushalt ist die Hagenuk Doppel-Heißluftfritteuse meine klare Empfehlung: 11 Liter Gesamtvolumen und zwei getrennte 5,5-Liter-Körbe sind praktisch, wenn Hauptgericht und Beilage gleichzeitig fertig werden sollen.',
          productsByHandles(catalog, ['hagenuk-doppel-heissluftfritteuse', 'heissluftfritteuse-mit-rotisserie']),
          ['Zwei Körbe genauer erklären', 'Mit Rotisserie vergleichen', 'Gericht für die Familie']
        );
      }

      if (people >= 3) {
        return baseResult(
          'Für drei bis vier Personen kommen zwei Modelle besonders infrage: das 8-Liter-Rotisserie-Modell für Drehspieß und rotierenden Korb oder die 11-Liter-Doppel-Heißluftfritteuse, wenn zwei Gerichte gleichzeitig fertig werden sollen. Welche dieser beiden Anwendungen ist Ihnen wichtiger?',
          productsByHandles(catalog, ['heissluftfritteuse-mit-rotisserie', 'hagenuk-doppel-heissluftfritteuse']),
          ['Rotisserie ist wichtiger', 'Zwei Gerichte gleichzeitig', 'Modelle direkt vergleichen']
        );
      }

      if (people > 0) {
        return baseResult(
          'Für ein bis zwei Personen würde ich zuerst die kompaktere Hagenuk Infrarot-Heißluftfritteuse ansehen: 5 Liter, 12 Programme und ein Sichtfenster passen gut zu kleineren Portionen. Wenn Sie häufig den Drehspieß nutzen möchten, ist das 8-Liter-Rotisserie-Modell die Alternative.',
          productsByHandles(catalog, ['infrarot-heissluftfritteuse', 'heissluftfritteuse-mit-rotisserie']),
          ['Welches Modell ist leiser?', 'Rezept für zwei Personen', 'Rotisserie ist wichtig']
        );
      }

      return baseResult(
        'Gern. Für wie viele Personen kochen Sie meistens? Danach sage ich Ihnen direkt, ob das 5-Liter-, 8-Liter-Rotisserie- oder 11-Liter-Doppelmodell besser passt.',
        [],
        ['Für 1-2 Personen', 'Für 3-4 Personen', 'Für 5 oder mehr Personen']
      );
    }

    const coffeeContext = /\b(kaffee|kaffeemaschine|espresso|filterkaffee|mahlwerk|milchgetraenk)\b/.test(normalizedMessage)
      || (/\b(filter|gemahlen|milch|tassen)\b/.test(normalizedMessage) && /\b(kaffee|kaffeemaschine|espresso)\b/.test(priorUserConversation));

    if (coffeeContext) {
      if (/\b(espresso|milchgetraenk|cappuccino|latte)\b/.test(normalizedMessage)) {
        return baseResult(
          'Für Espresso und Milchgetränke passt die Hagenuk Espressomaschine mit Milchaufschäumer. Die aktuelle Produktbeschreibung nennt 15 bar Druck, einen 1,2-Liter-Wassertank und einen separaten 700-ml-Milchtank.',
          productsByHandles(catalog, ['kaffeemaschine-mit-mahlwerk-2']),
          ['Produkt ansehen', 'Mit Filterkaffee vergleichen', 'Für mehrere Tassen?']
        );
      }

      if (/\b(frisch gemahlen|mahlwerk|bohnen)\b/.test(normalizedMessage)) {
        return baseResult(
          'Wenn frisch gemahlener Kaffee im Mittelpunkt steht, ist die Kaffeemaschine mit Edelstahl-Mahlwerk die passende Wahl. Laut Produktbeschreibung bereitet sie bis zu vier Tassen zu und bietet eine grobe oder feine Mahlstufe.',
          productsByHandles(catalog, ['kaffeemaschine-mit-mahlwerk']),
          ['Produkt ansehen', 'Mit Filtermaschine vergleichen', 'Vier Tassen reichen aus']
        );
      }

      if (/\b(filterkaffee|filter|klassisch|viele tassen)\b/.test(normalizedMessage)) {
        return baseResult(
          'Für klassischen Filterkaffee und mehrere Tassen ist die digitale Kaffeemaschine die passendere Wahl. Die aktuelle Produktbeschreibung nennt 1,5 Liter, bis zu 12 Tassen, Timer- und Warmhaltefunktion.',
          productsByHandles(catalog, ['kaffeemaschine-digital']),
          ['Produkt ansehen', 'Frisch gemahlen vergleichen', 'Timer genauer erklären']
        );
      }

      return baseResult(
        'Welche Art Kaffee trinken Sie am häufigsten: klassischen Filterkaffee, frisch gemahlenen Kaffee oder Espresso und Milchgetränke?',
        [],
        ['Filterkaffee', 'Frisch gemahlener Kaffee', 'Espresso und Milchgetränke']
      );
    }

    const frozenYogurtIntent = /\b(frozen yogurt|frozen yoghurt|joghurt eis|joghurteis|yogurtlu dondurma)\b/.test(normalizedMessage);
    const comparesDessertMachines = /\b(vergleich|vergleiche|vergleichen|unterschied|beide|alle|welche|auswahl|sortiment)\b/.test(normalizedMessage)
      && /\b(eis|eiscreme|eismaschine|eismaschinen|dessert|dessertmaschine|dessertmaschinen|slush)\b/.test(normalizedMessage);
    const genericDessertCategory = /\b(eismaschine|eismaschinen|dessertmaschine|dessertmaschinen|eisgeraet|eisgeraete)\b/.test(normalizedMessage);
    const dessertContext = frozenYogurtIntent
      || /\b(eis|eiscreme|eismaschine|eismaschinen|dessert|dessertmaschine|dessertmaschinen|slush|sorbet|gelato|milkshake)\b/.test(normalizedMessage)
      || (/\b(cremig|getraenk|party|rezept|andere|zweite|beide)\b/.test(normalizedMessage) && priorDessertContext);

    if (dessertContext) {
      if (comparesDessertMachines) {
        return baseResult(
          'Im aktuellen Hagenuk Sortiment gibt es zwei unterschiedliche Eisgeräte. Für Eiscreme, Gelato, Sorbet, Frozen Yogurt und Smoothie Bowls ist die Hagenuk Eiscreme & Dessertmaschine die richtige Wahl. Die Slush Ice Maschine arbeitet dagegen mit Direktkühlung und ist auf Slush, Milchshakes und Frozen-Cocktails mit bis zu 2,2 Litern ausgelegt. Für cremige Desserts: Eiscreme & Dessertmaschine. Für Getränke und größere Partymengen: Slush Ice Maschine.',
          dessertMachines,
          ['Frozen-Yogurt-Rezept', 'Slush ohne Alkohol', 'Welche ist familientauglicher?']
        );
      }

      if (frozenYogurtIntent || /\b(frozen yogurt rezept|joghurteis rezept)\b/.test(normalizedMessage)) {
        return baseResult(
          'Für Frozen Yogurt ist die Hagenuk Eiscreme & Dessertmaschine die passende Wahl. Startrezept für einen Becher: 350 g Natur- oder griechischen Joghurt, 60-80 g Honig oder Zucker, 1 TL Zitronensaft und optional 100 g pürierte Beeren glatt rühren. Die Basis im Becher vollständig einfrieren, anschließend das Frozen-Yogurt-Programm wählen und bei Bedarf RE-SPIN nutzen. Stückchen danach über EXTRAS einarbeiten.',
          productsByHandles(catalog, ['hagenuk-eiscreme-dessertmaschine-icm-500-05lb-800w']),
          ['Rezept ohne Zucker', 'Erdbeer-Frozen-Yogurt', 'Mit Slush vergleichen']
        );
      }

      if (/\b(slush|frozen drink|frozen cocktail|partygetraenk)\b/.test(normalizedMessage)) {
        return baseResult(
          'Für Slush, Milchshakes und Frozen-Cocktails ist die Hagenuk Slush Ice Maschine die passende Wahl. Die aktuelle Produktbeschreibung nennt 2,2 Liter Kapazität, Direktkühlung und eine Zubereitungszeit von ungefähr 30 Minuten.',
          productsByHandles(catalog, ['hagenuk-slush-ice-maschine-sim-200-19']),
          ['Slush ohne Alkohol', 'Mit der Dessertmaschine vergleichen', 'Produkt ansehen']
        );
      }

      if (/\b(was kann|was macht|wofuer|moeglichkeiten|programme|zubereiten)\b/.test(normalizedMessage)) {
        if (genericDessertCategory && !/\b(dessertmaschine|eiscreme)\b/.test(normalizedMessage)) {
          return baseResult(
            'Bei Hagenuk gibt es dafür zwei unterschiedliche Maschinen: Die Hagenuk Eiscreme & Dessertmaschine verarbeitet vorbereitete, gefrorene Becher zu cremigen Desserts wie Eiscreme, Gelato, Sorbet und Frozen Yogurt. Die Slush Ice Maschine kühlt Getränke direkt und ist für Slush, Milchshakes und Frozen-Cocktails ausgelegt. Welche Funktion meinen Sie?',
            dessertMachines,
            ['Cremiges Eis und Frozen Yogurt', 'Slush und Frozen Drinks', 'Beide Preise anzeigen']
          );
        }

        return baseResult(
          'Mit der Hagenuk Eiscreme & Dessertmaschine können Sie laut aktueller Produktbeschreibung unter anderem Eiscreme, Lite Ice Cream, Sorbet, Gelato, Frozen Yogurt, Frozen Drinks, Milkshakes und Smoothie Bowls zubereiten. RE-SPIN ist für eine cremigere Konsistenz vorgesehen; Stückchen können danach über EXTRAS eingearbeitet werden.',
          productsByHandles(catalog, ['hagenuk-eiscreme-dessertmaschine-icm-500-05lb-800w']),
          ['Frozen-Yogurt-Rezept', 'Frucht-Sorbet', 'Unterschied zur Slush-Maschine']
        );
      }

      return baseResult(
        'Im aktuellen Hagenuk Sortiment gibt es zwei unterschiedliche Eisgeräte: die Hagenuk Eiscreme & Dessertmaschine für cremige Ergebnisse wie Eiscreme, Gelato, Sorbet und Frozen Yogurt sowie die Slush Ice Maschine für Slush, Milchshakes und Frozen Drinks. Welche Richtung passt besser zu Ihnen?',
        dessertMachines,
        ['Cremiges Eis und Frozen Yogurt', 'Slush und Frozen Drinks', 'Beide Maschinen vergleichen']
      );
    }

    const referencesCurrentProduct = currentProductHandle
      && /\b(dieses produkt|dieses geraet|der artikel|auf dieser seite)\b/.test(normalizedMessage);
    const currentProduct = referencesCurrentProduct
      ? catalog.find((product) => product.handle === currentProductHandle)
      : null;
    const matches = currentProduct ? [currentProduct] : searchCatalog(catalog, message, 3);
    const wantsPostenland = /\b(postenland|anderer shop|andere quelle|wo sonst|alternative kaufen)\b/.test(normalizedMessage);
    const externalLink = wantsPostenland
      ? {
          label: 'Passende Angebote bei Postenland',
          url: `https://www.postenland.de/search?query=${encodeURIComponent(message || 'Hagenuk')}`
        }
      : null;

    if (matches.length === 1) {
      const description = productDescription(matches[0]);
      return baseResult(
        `Zu Ihrer Frage passt ${matches[0].title} am besten.${description ? ` ${description}` : ''} Möchten Sie dazu die Anwendung, die wichtigsten Unterschiede oder eine konkrete Kaufempfehlung klären?`,
        matches,
        ['Anwendung erklären', 'Mit Alternativen vergleichen', 'Produkt ansehen'],
        externalLink
      );
    }

    if (matches.length > 1) {
      return baseResult(
        'Diese Modelle passen im aktuellen Hagenuk Sortiment am besten zu Ihrer Frage. Sagen Sie mir noch, welche Anwendung oder welches Ergebnis Ihnen am wichtigsten ist; dann grenze ich die Auswahl auf eine klare Empfehlung ein.',
        matches,
        ['Modelle vergleichen', 'Welches passt zu mir?', 'Preis und Verfügbarkeit'],
        externalLink
      );
    }

    const fallbackReplies = [
      'Ich möchte Sie nicht mit einer Standardantwort abspeisen. Geben Sie mir einen kleinen Anhaltspunkt - ein Gericht, eine Zutat, ein Gerät oder die Anzahl der Personen - dann antworte ich konkret.',
      'Ich bin noch nicht ganz sicher, worauf Sie hinausmöchten. Geht es eher um etwas, das Sie zubereiten möchten, oder um die Wahl zwischen Hagenuk Geräten?',
      'Lassen Sie uns das kurz sortieren: Sie können mir frei beschreiben, was in Ihrer Küche passieren soll. Ich trenne dann Rezeptidee, passendes Gerät und Kaufentscheidung für Sie.'
    ];

    return baseResult(
      fallbackReplies[conversationState.turn % fallbackReplies.length],
      [],
      ['Mit einer Zutat starten', 'Gerät finden', 'Ein Rezept vorschlagen'],
      externalLink
    );
  };

  const buildLocalizedRecipeResult = (message, language) => {
    const recipes = searchRecipes(message, 3);

    if (!recipes.length) {
      return language === 'tr'
        ? baseResult('Bu istek için tarif kataloğunda henüz net bir eşleşme bulamadım. Ana malzemeyi, süreyi veya elinizdeki Hagenuk cihazını yazabilirsiniz.', [], ['Vegan tarifler', '15 dakikadan kısa', 'Cihaza göre ara'])
        : baseResult('I could not find a precise match in the recipe catalog yet. Tell me the main ingredient, available time, or your Hagenuk appliance and I will narrow it down.', [], ['Vegan recipes', 'Under 15 minutes', 'Search by appliance']);
    }

    const first = recipes[0];
    const reply = language === 'tr'
      ? `${recipes.length} uygun tarif buldum. İlk önerim ${first.title}: ${first.productName} ile ${formatRecipeDuration(first.totalMinutes)}. Tarif kartından tüm mutfak tarifini, cihaz bağlantısından da doğru ürün sayfasını açabilirsiniz.`
      : `I found ${recipes.length} matching recipes. My first suggestion is ${first.title}, prepared with ${first.productName} in ${formatRecipeDuration(first.totalMinutes)}. Open the full kitchen recipe or the correct appliance page directly from the card.`;
    const suggestionItems = language === 'tr'
      ? ['Sadece vegan', '15 dakikadan kısa', `${first.product?.shortName || first.productName} ile daha fazla`]
      : ['Vegan only', 'Under 15 minutes', `More with ${first.product?.shortName || first.productName}`];

    conversationState.lastRecipeQuery = message;
    return baseResult(reply, [], suggestionItems, null, recipes);
  };

  const generateAvaResponse = async (message) => {
    const intent = classifyIntent(message);

    if (!locale.startsWith('tr') && !locale.startsWith('en') && ['greeting', 'smalltalk', 'recipe'].includes(intent)) {
      loadCatalog();
      return germanAvaResponse(message, catalogCache);
    }

    if (locale.startsWith('tr')) {
      const normalizedMessage = normalize(message);

      if (GREETING_PATTERN.test(normalizedMessage)) {
        return baseResult(
          'Aa, merhaba. Ben HAGENUK AI. Bir yemek fikrinden, elinizdeki malzemeden veya doğru cihazı seçmekten başlayabiliriz. Bugün ne yapmak istersiniz?',
          [],
          ['Bugün ne pişirsem?', 'Vegan tarif', 'Cihaz seçimi']
        );
      }

      if (intent === 'recipe') return buildLocalizedRecipeResult(message, 'tr');

      const catalog = await loadCatalog();

      const matches = searchCatalog(catalog, message, 3);
      return matches.length > 0
        ? baseResult(
            'Canlı Hagenuk ürün kataloğunda sorunuza en yakın seçenekler bunlar. Kullanım amacınızı ve kaç kişilik olduğunu söylerseniz tek bir net öneriye indirebilirim.',
            matches,
            ['Modelleri karşılaştır', 'Hangisi bana uygun?', 'Ürünü incele']
          )
        : baseResult(
            'Size doğru ürünü önerebilmem için ne hazırlamak istediğinizi ve cihazı kaç kişinin kullanacağını yazar mısınız?',
            [],
            ['Airfryer', 'Kahve', 'Dondurma ve tatlı']
          );
    }

    if (locale.startsWith('en')) {
      const normalizedMessage = normalize(message);

      if (GREETING_PATTERN.test(normalizedMessage)) {
        return baseResult(
          'Ah, hello. I am HAGENUK AI. We can start with a dish, an ingredient, or the appliance that best fits your routine. What is on your mind today?',
          [],
          ['What should I cook?', 'Vegan ideas', 'Choose an appliance']
        );
      }

      if (intent === 'recipe') return buildLocalizedRecipeResult(message, 'en');

      const catalog = await loadCatalog();

      const matches = searchCatalog(catalog, message, 3);
      return matches.length > 0
        ? baseResult(
            'These are the closest matches from the live Hagenuk catalog. Tell me your main use case and household size, and I will narrow them down to one clear recommendation.',
            matches,
            ['Compare these models', 'Which one fits me?', 'View product']
          )
        : baseResult(
            'Tell me what you would like to prepare and how many people will use the appliance, and I will recommend the best-fitting Hagenuk model.',
            [],
            ['Air frying', 'Coffee', 'Ice cream and desserts']
          );
    }

    const catalog = await loadCatalog();
    return germanAvaResponse(message, catalog);
  };

  const openPanel = () => {
    panel.hidden = false;
    bindViewportTracking();
    document.documentElement.classList.add('hagenuk-ai-open');
    root.classList.remove('is-launcher-attention');
    requestAnimationFrame(() => {
      root.classList.add('is-open');
      window.dispatchEvent(new CustomEvent('hagenuk:ai-state', {
        detail: { open: true }
      }));
    });
    launcher.setAttribute('aria-expanded', 'true');
    try {
      input.focus({ preventScroll: true });
    } catch (_error) {
      input.focus();
    }
  };

  const closePanel = () => {
    root.classList.remove('is-open');
    document.documentElement.classList.remove('hagenuk-ai-open');
    unbindViewportTracking();
    window.dispatchEvent(new CustomEvent('hagenuk:ai-state', {
      detail: { open: false }
    }));
    launcher.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      panel.hidden = true;
      launcher.focus({ preventScroll: true });
    }, 220);
  };

  const scrollToLatest = () => {
    requestAnimationFrame(() => {
      conversation.scrollTop = conversation.scrollHeight;
    });
  };

  const createAssistantMark = () => {
    const mark = document.createElement('div');
    mark.className = 'hagenuk-ai__message-mark';
    mark.setAttribute('aria-hidden', 'true');

    if (avatarSrc) {
      const image = document.createElement('img');
      image.src = avatarSrc;
      image.alt = '';
      image.width = 40;
      image.height = 40;
      image.decoding = 'async';
      mark.append(image);
    }

    return mark;
  };

  const appendMessage = (role, text) => {
    const message = document.createElement('div');
    message.className = `hagenuk-ai__message hagenuk-ai__message--${role}`;

    if (role === 'assistant') {
      message.append(createAssistantMark());
    }

    const copy = document.createElement('p');
    copy.textContent = text;
    message.append(copy);
    conversation.append(message);
    scrollToLatest();
    return message;
  };

  const appendTyping = (sourceMessage) => {
    const message = document.createElement('div');
    message.className = 'hagenuk-ai__message hagenuk-ai__message--assistant hagenuk-ai__message--typing';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-label', labels.loading);

    const intent = classifyIntent(sourceMessage);
    const thinkingLabel = intent === 'recipe'
      ? labels.thinkingRecipe
      : ['product', 'comparison'].includes(intent)
        ? labels.thinkingProduct
        : labels.thinkingConversation;

    const dots = document.createElement('div');
    dots.className = 'hagenuk-ai__typing';
    dots.innerHTML = `<span class="hagenuk-ai__typing-label">${thinkingLabel}</span><span class="hagenuk-ai__typing-dots" aria-hidden="true"><i></i><i></i><i></i></span>`;

    message.append(createAssistantMark(), dots);
    conversation.append(message);
    scrollToLatest();
    return message;
  };

  const formatPrice = (price) => {
    if (!price || !Number.isFinite(Number(price.amount))) {
      return '';
    }

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: price.currency || 'EUR'
      }).format(Number(price.amount));
    } catch (_error) {
      return `${Number(price.amount).toFixed(2)} ${price.currency || 'EUR'}`;
    }
  };

  const notifyCart = async (responseJson, response) => {
    document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: {
        status: response.ok ? 'success' : 'error',
        error: responseJson.description || ''
      }
    }));

    if (!response.ok) {
      return;
    }

    const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
    const cart = await cartResponse.json();
    cart.sections = responseJson.sections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart }
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        cart,
        openMiniCart: window.themeVariables.settings.cartType === 'drawer'
      }
    }));
  };

  const addToCart = async (button, variantId) => {
    if (!variantId || button.disabled) {
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      body.append('sections', 'mini-cart');

      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      const responseJson = await response.json();

      await notifyCart(responseJson, response);

      if (response.ok) {
        button.classList.add('is-added');
        button.querySelector('span').textContent = labels.added;
      } else {
        button.disabled = false;
      }
    } catch (_error) {
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const appendProducts = (products) => {
    if (!Array.isArray(products) || products.length === 0) {
      lastProductSignature = '';
      return;
    }

    const visibleProducts = products.slice(0, 3);
    const signature = visibleProducts
      .map((product) => product.handle || product.title || '')
      .join('|');

    if (signature && signature === lastProductSignature) {
      return;
    }

    lastProductSignature = signature;
    const list = document.createElement('div');
    list.className = 'hagenuk-ai__products';

    visibleProducts.forEach((product) => {
      const card = document.createElement('article');
      card.className = 'hagenuk-ai__product';

      const imageLink = document.createElement('a');
      imageLink.className = 'hagenuk-ai__product-image';
      imageLink.href = product.url;
      imageLink.setAttribute('aria-label', `${labels.details}: ${product.title}`);

      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.image_alt || product.title;
        image.loading = 'eager';
        image.decoding = 'async';
        image.width = 96;
        image.height = 96;
        imageLink.append(image);
      }

      const content = document.createElement('div');
      content.className = 'hagenuk-ai__product-content';

      const title = document.createElement('a');
      title.className = 'hagenuk-ai__product-title';
      title.href = product.url;
      title.textContent = product.title;

      const price = document.createElement('p');
      price.className = 'hagenuk-ai__product-price';
      price.textContent = formatPrice(product.price);

      const actions = document.createElement('div');
      actions.className = 'hagenuk-ai__product-actions';

      const details = document.createElement('a');
      details.className = 'hagenuk-ai__product-link';
      details.href = product.url;
      details.textContent = labels.details;
      actions.append(details);

      const cartButton = document.createElement('button');
      cartButton.type = 'button';
      cartButton.className = 'hagenuk-ai__cart-button';
      cartButton.disabled = !product.available || !product.variant_id;
      cartButton.innerHTML = `
        <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="20" r="1"/><circle cx="19" cy="20" r="1"/>
          <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L21 8H6"/>
        </svg>
        <span>${product.available ? labels.cart : labels.unavailable}</span>`;
      cartButton.addEventListener('click', () => addToCart(cartButton, product.variant_id));
      actions.append(cartButton);

      content.append(title, price, actions);
      card.append(imageLink, content);
      list.append(card);
    });

    conversation.append(list);
    scrollToLatest();
  };

  const appendRecipes = (recipes) => {
    if (!Array.isArray(recipes) || recipes.length === 0) {
      lastRecipeSignature = '';
      return;
    }

    const visibleRecipes = recipes.slice(0, 3);
    const signature = visibleRecipes.map((recipe) => recipe.id || recipe.title || '').join('|');

    if (signature && signature === lastRecipeSignature) return;

    lastRecipeSignature = signature;
    const list = document.createElement('div');
    list.className = 'hagenuk-ai__recipes';

    visibleRecipes.forEach((recipe) => {
      const card = document.createElement('article');
      card.className = 'hagenuk-ai__recipe';

      const meta = document.createElement('div');
      meta.className = 'hagenuk-ai__recipe-meta';
      const diet = recipe.displayDiet || (recipe.diet === 'vegan'
        ? 'Vegan'
        : recipe.diet === 'vegetarisch'
          ? 'Vegetarisch'
          : 'Fleisch & Fisch');
      meta.innerHTML = `<span>${recipe.category || 'Rezept'}</span><span>${formatRecipeDuration(recipe.totalMinutes)}</span><span>${diet}</span>`;

      const title = document.createElement('a');
      title.className = 'hagenuk-ai__recipe-title';
      title.href = recipe.url;
      title.textContent = recipe.title;

      const hook = document.createElement('p');
      hook.className = 'hagenuk-ai__recipe-hook';
      hook.textContent = truncate(recipe.hook, 150);

      const appliance = document.createElement('a');
      appliance.className = 'hagenuk-ai__recipe-appliance';
      appliance.href = recipe.productUrl || recipe.product?.url || '#';
      appliance.setAttribute('aria-label', `${labels.appliance}: ${recipe.productName}`);

      if (recipe.image) {
        const image = document.createElement('img');
        image.src = recipe.image;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 58;
        image.height = 58;
        appliance.append(image);
      }

      const applianceCopy = document.createElement('span');
      const applianceLabel = document.createElement('small');
      applianceLabel.textContent = labels.appliance;
      const applianceName = document.createElement('strong');
      applianceName.textContent = recipe.product?.shortName || recipe.productName;
      applianceCopy.append(applianceLabel, applianceName);
      appliance.append(applianceCopy);

      const actions = document.createElement('div');
      actions.className = 'hagenuk-ai__recipe-actions';
      const recipeLink = document.createElement('a');
      recipeLink.href = recipe.url;
      recipeLink.textContent = labels.recipe;
      const productLink = document.createElement('a');
      productLink.href = recipe.productUrl || recipe.product?.url || '#';
      productLink.textContent = labels.appliance;
      actions.append(recipeLink, productLink);

      card.append(meta, title, hook, appliance, actions);
      list.append(card);
    });

    conversation.append(list);
    scrollToLatest();
  };

  const appendExternalLink = (link) => {
    if (!link || typeof link.url !== 'string') {
      return;
    }

    let url;

    try {
      url = new URL(link.url);
    } catch (_error) {
      return;
    }

    if (url.protocol !== 'https:' || !['postenland.de', 'www.postenland.de'].includes(url.hostname)) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'hagenuk-ai__external';

    const anchor = document.createElement('a');
    anchor.href = url.toString();
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.textContent = typeof link.label === 'string' && link.label.trim()
      ? link.label.trim()
      : labels.external;
    anchor.setAttribute('aria-label', `${anchor.textContent} (${labels.external})`);

    wrapper.append(anchor);
    conversation.append(wrapper);
    scrollToLatest();
  };

  const renderSuggestions = (items) => {
    if (!suggestions || !Array.isArray(items)) {
      return;
    }

    suggestions.classList.remove('hagenuk-ai__quick-actions--primary');
    suggestions.replaceChildren();

    items.slice(0, 3).forEach((text) => {
      if (typeof text !== 'string' || !text.trim()) {
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = text.trim();
      button.addEventListener('click', () => submitMessage(text.trim()));
      suggestions.append(button);
    });

    conversation.append(suggestions);
    scrollToLatest();
  };

  const setBusy = (busy) => {
    isBusy = busy;
    input.disabled = busy;
    sendButton.disabled = busy;
    sendButton.toggleAttribute('aria-busy', busy);
  };

  const submitMessage = async (value) => {
    const message = (value || input.value).trim().slice(0, 600);

    if (!message || isBusy) {
      return;
    }

    input.value = '';
    input.style.height = '';
    appendMessage('user', message);
    setBusy(true);
    const typing = appendTyping(message);

    try {
      const data = await generateAvaResponse(message);

      typing.remove();
      appendMessage('assistant', data.reply);
      appendProducts(data.products);
      appendRecipes(data.recipes);
      appendExternalLink(data.external_link);
      renderSuggestions(data.suggestions);

      conversationState.turn += 1;
      conversationState.lastIntent = classifyIntent(message);
      conversationState.lastRecipeIds = Array.isArray(data.recipes)
        ? data.recipes.map((recipe) => recipe.id).filter(Boolean).slice(0, 3)
        : [];

      history.push(
        { role: 'user', content: message },
        {
          role: 'assistant',
          content: data.reply,
          product_handles: Array.isArray(data.products)
            ? data.products.map((product) => product.handle).filter(Boolean).slice(0, 3)
            : [],
          recipe_ids: conversationState.lastRecipeIds
        }
      );

      if (history.length > 8) {
        history.splice(0, history.length - 8);
      }
    } catch (_error) {
      typing.remove();
      appendMessage('assistant', labels.error);
    } finally {
      setBusy(false);
      input.focus({ preventScroll: true });
    }
  };

  launcher.addEventListener('click', openPanel);
  closeButton.addEventListener('click', closePanel);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submitMessage();
  });

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 112)}px`;
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  });

  root.addEventListener('click', (event) => {
    const prompt = event.target.closest('[data-hagenuk-ai-prompt]');
    if (prompt) {
      submitMessage(prompt.dataset.hagenukAiPrompt);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('is-open')) {
      closePanel();
    }
  });

  window.setTimeout(() => {
    if (!root.classList.contains('is-open')) {
      root.classList.add('is-launcher-attention');
      window.setTimeout(() => root.classList.remove('is-launcher-attention'), 1800);
    }
  }, 7800);
})();
